

"use client";

import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "Assessment/src/lib/utils";
import { useColor } from "components/ui/ColorContext";

/* ----------------------------------
   Root + Trigger
----------------------------------- */

const AlertDialog = AlertDialogPrimitive.Root;
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
const AlertDialogPortal = AlertDialogPrimitive.Portal;

/* ----------------------------------
   Overlay
----------------------------------- */

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
      className
    )}
    {...props}
  />
));
AlertDialogOverlay.displayName =
  AlertDialogPrimitive.Overlay.displayName;

/* ----------------------------------
   Content (GLASS + GLOW)
----------------------------------- */

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, style, ...props }, ref) => {
  const { primaryColor } = useColor();

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
       style={{
  background: `
    linear-gradient(
      135deg,
      ${primaryColor}55 0%,
      ${primaryColor}33 35%,
      rgba(0, 0, 0, 0.25) 70%,
      rgba(0, 0, 0, 0.45) 100%
    )
  `,
  borderColor: `${primaryColor}aa`,
  boxShadow: `
    0 0 0 1px ${primaryColor}66,
    0 0 30px ${primaryColor}88,
    inset 0 0 25px ${primaryColor}33
  `,
}}
        className={cn(
          `
          fixed left-1/2 top-1/2 z-50
          w-full max-w-md
          -translate-x-1/2 -translate-y-1/2
          rounded-2xl
          border
          bg-black/60
          backdrop-blur-xl
          p-6
          text-white
          shadow-lg
          outline-none
          data-[state=open]:animate-in
          data-[state=closed]:animate-out
          data-[state=open]:fade-in-0
          data-[state=closed]:fade-out-0
          data-[state=open]:zoom-in-95
          data-[state=closed]:zoom-out-95
          `,
          className
        )}
        {...props}
      />
    </AlertDialogPortal>
  );
});
AlertDialogContent.displayName =
  AlertDialogPrimitive.Content.displayName;

/* ----------------------------------
   Header / Footer
----------------------------------- */

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-2 text-center", className)}
    {...props}
  />
);

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
);

/* ----------------------------------
   Title
----------------------------------- */

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, style, ...props }, ref) => {
  const { primaryColor } = useColor();

  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      style={{
        textShadow: `0 0 14px ${primaryColor}90`,
        ...style,
      }}
      className={cn("text-xl font-semibold", className)}
      {...props}
    />
  );
});
AlertDialogTitle.displayName =
  AlertDialogPrimitive.Title.displayName;

/* ----------------------------------
   Description
----------------------------------- */

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-white/70", className)}
    {...props}
  />
));
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

/* ----------------------------------
   Action Button (PRIMARY)
----------------------------------- */

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, style, ...props }, ref) => {
  const { primaryColor } = useColor();

  return (
    <AlertDialogPrimitive.Action
      ref={ref}
      style={{
        backgroundColor: primaryColor,
        boxShadow: `0 0 18px ${primaryColor}90`,
        ...style,
      }}
      className={cn(
        "rounded-lg px-4 py-2 text-sm font-medium text-black",
        "transition hover:opacity-90 focus:outline-none",
        className
      )}
      {...props}
    />
  );
});
AlertDialogAction.displayName =
  AlertDialogPrimitive.Action.displayName;

/* ----------------------------------
   Cancel Button (OUTLINE)
----------------------------------- */

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, style, ...props }, ref) => {
  const { primaryColor } = useColor();

  return (
    <AlertDialogPrimitive.Cancel
      ref={ref}
      style={{
        borderColor: primaryColor,
        color: primaryColor,
        ...style,
      }}
      className={cn(
        "rounded-lg border bg-transparent px-4 py-2 text-sm",
        "transition hover:bg-white/5 focus:outline-none",
        className
      )}
      {...props}
    />
  );
});
AlertDialogCancel.displayName =
  AlertDialogPrimitive.Cancel.displayName;

/* ----------------------------------
   Exports
----------------------------------- */

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
